import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/shared/Button";

import MotionDiv from "@/components/animations/MotionDiv";
import { fadeUp, stagger } from "@/animations/variants";
import { AnimatePresence, motion } from "framer-motion";

import { useScreens } from "@/hooks/useScreens";

const DesignSection = () => {
  const { t, i18n } = useTranslation();
  const isAR = i18n.resolvedLanguage?.startsWith("ar");

  const { screens, loading, error } = useScreens("web", isAR);

  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (screens.length > 0 && !activeId) {
      setActiveId(screens[0].id);
    }
  }, [screens, activeId]);

  const activeScreen =
    screens.find((item) => item.id === activeId) || screens[0];

  return (
    <section
      id="design"
      dir={isAR ? "rtl" : "ltr"}
      className="mx-auto flex max-w-7xl flex-col px-4 py-16"
    >
      {/* HEADER */}
      <MotionDiv
        variants={fadeUp}
        className="mx-auto max-w-4xl text-center md:px-8"
      >
        <h2 className="text-primary text-3xl font-bold md:text-5xl">
          {t("designSection.title")}
        </h2>

        <p className="text-gray mt-5 text-base font-medium md:text-lg">
          {t("designSection.subtitle")}
        </p>
      </MotionDiv>

      {loading && (
        <p className="mt-10 text-center text-sm text-gray-500">
          {isAR ? "جاري تحميل الشاشات..." : "Loading screens..."}
        </p>
      )}

      {!loading && error && (
        <p className="mt-10 text-center text-sm text-red-500">
          {isAR ? "حدث خطأ أثناء تحميل الشاشات" : error}
        </p>
      )}

      {!loading && !error && screens.length === 0 && (
        <p className="mt-10 text-center text-sm text-gray-500">
          {isAR ? "لا توجد شاشات حالياً" : "No screens available"}
        </p>
      )}

      {!loading && !error && screens.length > 0 && (
        <div className="mt-16 flex flex-col gap-14 lg:flex-row lg:items-center">
          {/* SIDEBAR */}
          <MotionDiv variants={fadeUp} className="w-full lg:max-w-[280px]">
            <div className="flex overflow-x-auto rounded-2xl bg-[#c4daff38] p-3 lg:h-[520px] lg:flex-col lg:justify-center lg:overflow-visible lg:p-4">
              <MotionDiv
                variants={stagger}
                className="flex min-w-max flex-row gap-3 lg:min-w-0 lg:flex-col"
              >
                {screens.map((screen) => {
                  const isActive = activeScreen?.id === screen.id;

                  return (
                    <MotionDiv
                      key={screen.id}
                      variants={fadeUp}
                      className="w-full"
                    >
                      <Button
                        onClick={() => setActiveId(screen.id)}
                        className={`w-full rounded-xl px-4 py-2 text-center text-sm transition-all md:text-base ${
                          isActive
                            ? "!bg-primary font-medium !text-white"
                            : "!bg-white !text-primary hover:!bg-primary hover:!text-white"
                        }`}
                      >
                        {screen.title}
                      </Button>
                    </MotionDiv>
                  );
                })}
              </MotionDiv>
            </div>
          </MotionDiv>

          {/* IMAGE */}
          <div className="flex flex-1 items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeScreen?.id}
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="max-h-[520px] w-full lg:h-[520px]"
              >
                <img
                  src={activeScreen.image}
                  alt={activeScreen.title}
                  className="h-auto w-full object-contain lg:h-full"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}
    </section>
  );
};

export default DesignSection;